import styles from "./toolbox-component-font-picker.module.css";

import { useState, useRef, useEffect, useCallback } from "react";
import { ToolboxComponentDropdown as Dropdown } from "./toolbox-component-dropdown";
import { filterClasses } from "PosterEditor/utils/filter-classes";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { ToolboxComponentSection as Section } from "./toolbox-component-section";
import { useTouchMode } from "BEDROCK/mobile";

interface ToolboxComponentFontPickerProps {
  fontNames: string[];
  selectedFont?: string;
  onPreviewFont?: (fontName: string | undefined) => void;
  onSelectFont: (fontName: string) => void;
  className?: string;
  disabled?: boolean;
}

const MOBILE_FONT_PICKER_MAX_HEIGHT = 280;

export const ToolboxComponentFontPicker = (
  props: ToolboxComponentFontPickerProps
) => {
  const selectedFontRef = useRef<HTMLButtonElement | null>(null);
  const { project } = usePosterProject();
  const [isVisible, setIsVisible] = useState(false);
  const [previewingFont, setPreviewingFont] = useState<string | undefined>();
  const isTouchMode = useTouchMode();

  useEffect(() => {
    if (isVisible && selectedFontRef.current) {
      selectedFontRef.current.scrollIntoView({
        behavior: "auto",
        block: "start",
      });
    }
  }, [isVisible]);

  const usedFonts = () => {
    const fonts = new Set<string>();
    if (project) {
      fonts.add(project.staticLabelStyle.font);
      fonts.add(props.selectedFont ?? "");
    }
    return Array.from(fonts);
  };

  const handleToggleVisibility = useCallback(() => {
    setIsVisible((prev) => !prev);
  }, []);

  const onPreviewFont = (fontName: string | undefined) => {
    setPreviewingFont(fontName);
    if (props.onPreviewFont) {
      props.onPreviewFont(fontName);
    }
  };

  const label = (
    <div
      className={filterClasses(
        styles.fontPickerLabel,
        props.className,
        props.disabled ? styles.disabled : ""
      )}
    >
      <span
        style={{
          fontFamily: props.selectedFont ?? "sans-serif",
          fontSize: "20px",
        }}
      >
        {props.selectedFont ?? "Font"}
      </span>
      <img
        className={styles.expandChevron}
        src={
          !isVisible
            ? "/pallete-components/CHEVRON-DOWN-ICON.svg"
            : "/pallete-components/CHEVRON-UP-ICON.svg"
        }
        alt="Collapse tool Toolbox"
      />
    </div>
  );

  return (
    <Dropdown
      isVisible={isVisible}
      onToggleVisibility={handleToggleVisibility}
      label={label}
      onMouseLeave={() => onPreviewFont(undefined)}
      dropUp={isTouchMode}
      maxHeight={isTouchMode ? MOBILE_FONT_PICKER_MAX_HEIGHT : undefined}
    >
      <div className={styles.fontPickerContainer}>
        <div className={styles.sectionTitle}>Recent</div>

        {usedFonts().map((fontName) => (
          <button
            key={fontName}
            className={filterClasses(styles.fontName)}
            style={{ fontFamily: fontName }}
            onClick={() => {
              props.onSelectFont(fontName);
              setIsVisible(false);
            }}
            onMouseEnter={() => {
              onPreviewFont(fontName);
            }}
          >
            {fontName}
          </button>
        ))}
        <div className={styles.sectionTitle}>All fonts</div>
        {props.fontNames.map((fontName) => (
          <button
            key={fontName}
            ref={fontName === props.selectedFont ? selectedFontRef : null}
            className={filterClasses(
              styles.fontName,
              fontName === props.selectedFont ? styles.fontNameSelected : "",
              previewingFont === fontName && fontName !== props.selectedFont
                ? styles.fontNamePreview
                : ""
            )}
            style={{ fontFamily: fontName }}
            onClick={() => {
              props.onSelectFont(fontName);
              setIsVisible(false);
            }}
            onMouseEnter={() => {
              onPreviewFont(fontName);
            }}
          >
            {fontName}
          </button>
        ))}
      </div>
    </Dropdown>
  );
};
